import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Loader2, Lock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "@/hooks/useAnalytics";
import { useCurrentUser, useUpdatePassword } from "@/hooks/useProfileQueries";
import { z } from "zod";
import { passwordChangeSchema } from "@/lib/validations";
import { UnifiedMfaSection } from "@/components/security/UnifiedMfaSection";

interface SecurityFormContentProps {
  onSuccess?: () => void;
}

export function SecurityFormContent({ onSuccess }: SecurityFormContentProps) {
  const { t } = useTranslation();
  const { trackFeatureUsage } = useAnalytics();
  const { data: user } = useCurrentUser();
  const updatePasswordMutation = useUpdatePassword();

  const [passwords, setPasswords] = useState({
    current: "",
    new: "",
    confirm: "",
  });

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      passwordChangeSchema.parse({
        newPassword: passwords.new,
        confirmPassword: passwords.confirm,
      });
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast.error(error.errors[0].message);
        return;
      }
    }

    try {
      await updatePasswordMutation.mutateAsync(passwords.new);
      trackFeatureUsage('Password Changed', { user_id: user?.id });
      toast.success(t('common:profile.passwordSuccess', 'Password updated successfully'));
      setPasswords({ current: "", new: "", confirm: "" });
      onSuccess?.();
    } catch (_error) {
      toast.error(t('common:profile.passwordFailed', 'Failed to update password'));
    }
  };

  const isUpdating = updatePasswordMutation.isPending;

  return (
    <div className="space-y-6">
      {/* MFA Section */}
      {user?.id && <UnifiedMfaSection userId={user.id} />}

      {/* Password Change Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5 text-primary" />
            {t('profile.security.title', 'Change Password')}
          </CardTitle>
          <CardDescription>
            {t('profile.security.description', 'Update your password to keep your account secure')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new_password">{t('profile.security.newPassword', 'New Password')}</Label>
              <Input
                id="new_password"
                type="password"
                value={passwords.new}
                onChange={(e) => setPasswords({ ...passwords, new: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm_password">{t('profile.security.confirmPassword', 'Confirm New Password')}</Label>
              <Input
                id="confirm_password"
                type="password"
                value={passwords.confirm}
                onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })}
                required
              />
            </div>

            <Button type="submit" disabled={isUpdating}>
              {isUpdating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {t('profile.security.updatePassword', 'Update Password')}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
